import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import TourV2Itinerary from 'components/OfferPage/TourV2/Itinerary/TourV2Itinerary'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import { getTourV2VariationDuration } from 'lib/tours/tourUtils'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorTourOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import type { MouseEventHandler } from 'react'
import React, { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { last, sortBy } from 'lib/array/arrayUtils'

interface Props {
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
  variation?: App.Tours.TourV2OfferVariation
  filters?: App.OfferListFilters
}

function TourV2SearchTileMeta({ offer, variation, filters }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
  })

  const list = useMemo<Array<string>>(() => {
    if (variation) {
      return [
        intl.formatMessage(
          {
            id: 'CtbgI+KZkn',
            defaultMessage: '{count, plural, one {# day} other {# days}}',
            description: 'Number of days in tour variation duration on the tour page',
          },
          { count: getTourV2VariationDuration(variation) },
        ),
        intl.formatMessage(
          {
            id: 'PX6f4xHC7j',
            defaultMessage: '{count, plural, one {# place} other {# places}}',
            description: 'Number of places visited in tour variation on the tour page',
          },
          { count: variation.placesVisited.length },
        ),
        intl.formatMessage(
          {
            id: 'eT8sMsht9h',
            defaultMessage: '{count, plural, one {# country} other {# countries}}',
            description: 'Number of countries visited in tour variation in the tours section',
          },
          { count: variation.countriesVisited.length },
        ),
      ]
    }
    return []
  }, [variation, intl])

  const { firstDepartureDate, lastDepartureDate } = useMemo<{ firstDepartureDate: string, lastDepartureDate: string }>(() => {
    const sortedDepartures = sortBy(variation?.departures ?? [], (departure) => departure.startDate, 'asc')
    const firstDepartureDate = sortedDepartures[0].startDate
    const lastDepartureDate = last(sortedDepartures).startDate
    return { firstDepartureDate, lastDepartureDate }
  }, [variation])

  const hasOneDeparture = firstDepartureDate === lastDepartureDate

  const tripItineraryTitle = intl.formatMessage({
    id: '0uz301HUAi',
    defaultMessage: 'Trip itinerary',
    description: 'Modal title for tour trip itinerary on the tour offer detail page',
  })
  const viewOfferLabel = intl.formatMessage({
    id: 'ykK5S3C9+x',
    defaultMessage: 'View offer',
    description: 'Button text to view the tour offer details on the tour page',
  })
  const viewItineraryLabel = intl.formatMessage({
    id: 'a1cSvxR7u1',
    defaultMessage: 'View itinerary',
    description: 'Link text to open tour itinerary modal on the tour offer detail page',
  })

  const openItineraryModal = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()
    showModal(<Modal
      title={tripItineraryTitle}
      primaryActionText={viewOfferLabel}
      primaryActionTo={offerUrl}
    >
      <TourV2Itinerary
        itinerary={variation!.itinerary}
        offerType={offer.type}
        showPrint
        showExperiences={false}
      />
    </Modal>)
  }, [showModal, variation, offer, offerUrl, tripItineraryTitle, viewOfferLabel])

  const pointsEarnCalculationOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(offer)
      const daysCount = variation ? getTourV2VariationDuration(variation) : 0
      const nights = daysCount > 0 ? daysCount - 1 : undefined
      return [
        generateLoyaltyPointsCalculatorTourOptions(offer,
          {
            price: cheapestPurchasableOption?.price,
            memberPrice: cheapestPurchasableOption?.memberPrice,
            roomType: cheapestPurchasableOption?.roomType,
            packageId: cheapestPurchasableOption?.fkDepartureId,
            offerId: cheapestPurchasableOption?.fkTourId,
            nights,
          }),
      ]
    },
    [offer, variation],
  )

  return <Group direction="vertical" gap={4}>
    <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationOptions} calculationType="estimate" />
    <BodyText variant="medium" colour="neutral-two" weight="bold">{offer.operatedBy}</BodyText>
    {variation && <Group direction="horizontal" gap="4 8" wrap="wrap">
      <BodyText variant="medium">{list.join(formatMiddleDotSeparator(intl))}</BodyText>

      {!!variation.itinerary.length && <TextLink onClick={openItineraryModal} size="medium">{viewItineraryLabel}</TextLink>}
      <BodyText variant="medium">
        {hasOneDeparture && <FormattedMessage
          id="XI9E/droPi"
          defaultMessage="Travel on <bold>{departureDate}</bold>"
          description="Tour travel date when there is a single departure date"
          values={{
            bold: (chunks) => <b>{chunks}</b>,
            departureDate: <FormatDateTime value={firstDepartureDate} format="short-month-long-year" />,
          }}
        />}
        {!hasOneDeparture && <FormattedMessage
          id="Rbv51b5REY"
          defaultMessage="Travel from <bold>{startDate}</bold> to <bold>{endDate}</bold>"
          description="Tour travel date range showing first and last departure dates"
          values={{
            bold: (chunks) => <b>{chunks}</b>,
            startDate: <FormatDateTime value={firstDepartureDate} format="short-month-long-year" />,
            endDate: <FormatDateTime value={lastDepartureDate} format="short-month-long-year" />,
          }}
        />}
      </BodyText>
    </Group>}
  </Group>
}

export default TourV2SearchTileMeta
